import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';
import type { UserWithActions } from '~/modules/auth';

import { getTrackOverwrite } from '../../api/overwrites';
import { escapeAudioPresentation } from '../../api/related-tracks';
import type { TrackLock } from '../../api/track-lock';
import { Drawer } from '../../components/Drawer';
import type { DashboardItem } from '../../types';
import { Comment } from './components/Comment';
import { FieldBlock, FieldValue } from './components/Fields';
import { OverwriteIcon } from './components/OverwritenIcon';
import { QuickList } from './components/QuickList';
import { TableTd, TableTh } from './components/Table';
import { TrackOverwrite } from './components/TrackOverwrite';
import { RelatedTracks } from './RelatedTracks';
import { autoPluralize, countryRenderer } from './renderers';

const mothershipUrl = 'https://mothership.wmg.cloud';

interface GeneralInfoProps {
  isrc: string;
  data: DashboardItem;
  trackLock: TrackLock | null;
  handleTrackLock: () => Promise<void>;
  trackIgnore: TrackLock | null;
  handleTrackIgnore: () => Promise<void>;
  currentUser: Partial<UserWithActions>;
}

const GeneralInfo = (props: GeneralInfoProps): JSX.Element => {
  const { t } = useTranslation('label-perf');
  const [processing, setProcessing] = useState(false);
  const [typeToggle, setTypeToggle] = useState<'same' | 'all'>('same');
  const [overwriteDialog, setOverwriteDialog] = useState(false);
  const [overwrites, setOverwrites] = useState<{
    trackTitle?: string;
    artistName?: string;
  }>({});
  const { data, trackLock, trackIgnore, currentUser } = props;

  useEffect(() => {
    getTrackOverwrite(props.isrc)
      .then((_data) => {
        if (_data) {
          setOverwrites({
            trackTitle: _data.track_title,
            artistName: _data.artist_name,
          });
        }
        return null;
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching track overwrite data:', error);
        setOverwrites({}); // Reset overwrites on error
      });
  }, [props.isrc]);

  const handleTrackLock = (): void => {
    setProcessing(true);
    props
      .handleTrackLock()
      .then(() => {
        setProcessing(false);
        return null;
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleTrackIgnore = (): void => {
    setProcessing(true);
    props
      .handleTrackIgnore()
      .then(() => {
        setProcessing(false);
        return null;
      })
      .catch((err) => {
        console.error(err);
      });
  };

  return (
    <div className="grid min-w-[20em] grid-cols-1 gap-md">
      <div className="relative rounded-sm border p-sm bg-level-02 border-emphasis-subtle">
        {/* Track Info Section */}
        <div className="relative flex items-center p-sm">
          <h2 className="text-body-lg font-semibold text-white">
            {t('body.trackInfo')}
          </h2>

          {/* Lock button, floating above the grey background */}
          <div className="absolute right-sm flex">
            {processing ? (
              <div
                data-testid="processing-spinner"
                className="mt-xs flex items-center px-sm align-middle text-body-sm text-white"
              >
                <p>
                  <Icon
                    type="spinner"
                    variant="solid"
                    size="sm"
                    className="fa-spin mr-sm"
                  />
                  {t('body.processing')}
                </p>
              </div>
            ) : null}

            {!processing && (
              <button
                className="hover:bg-white ml-sm flex items-center px-sm py-xs text-center text-body-sm font-bold text-primary-default hover:text-info-hover"
                onClick={() => setOverwriteDialog(true)}
              >
                <Icon
                  type="marker"
                  variant="solid"
                  size="sm"
                  className="mr-sm"
                />
                {t('body.overwrites')}
              </button>
            )}

            {!processing && !trackLock && !trackIgnore && (
              <button
                className="hover:bg-white rounded ml-sm w-[10em] px-sm py-xs text-center text-body-sm font-bold bg-level-03 text-subtle hover:text-primary-hover"
                onClick={handleTrackLock}
              >
                <Icon type="eye" variant="solid" size="sm" className="mr-sm" />

                {t('In Review')}
              </button>
            )}

            {!processing && trackLock ? (
              <div className="ml-sm flex items-center align-middle text-body-sm text-white">
                {t('body.inReviewBy')}{' '}
                <span className="mx-xs font-bold">
                  {trackLock.created_by.email === currentUser.email
                    ? 'yourself'
                    : trackLock.created_by.name}
                </span>{' '}
                {t('on')}
                <span className="ml-xs font-semibold">
                  {new Date(trackLock.created_at).toLocaleDateString()}
                </span>
              </div>
            ) : null}

            {!processing &&
            trackLock &&
            trackLock.created_by.email === currentUser.email ? (
              <button
                className="rounded ml-sm w-[10em] px-sm py-xs text-center text-body-sm font-bold bg-error-default text-white hover:bg-error-pressed"
                onClick={handleTrackLock}
              >
                <Icon
                  type="eye-slash"
                  variant="solid"
                  size="sm"
                  className="mr-sm"
                />
                {t('actions.cancelReview')}
              </button>
            ) : null}

            {!processing && !trackLock && !trackIgnore && (
              <button
                className="hover:bg-white rounded ml-sm w-[10em] px-sm py-xs text-center text-body-sm font-bold bg-level-05 text-subtle hover:text-emphasis-subtle"
                onClick={handleTrackIgnore}
              >
                <Icon type="ban" variant="solid" size="sm" className="mr-sm" />
                {t('actions.disable')}
              </button>
            )}

            {!processing && trackIgnore ? (
              <div className="ml-sm flex items-center align-middle text-body-sm text-white">
                {t('body.disabledBy')}{' '}
                <span className="mx-xs font-bold">
                  {trackIgnore.created_by.email === currentUser.email
                    ? 'yourself'
                    : trackIgnore.created_by.name}
                </span>{' '}
                {t('on')}
                <span className="ml-xs font-semibold">
                  {new Date(trackIgnore.created_at).toLocaleDateString()}
                </span>
              </div>
            ) : null}

            {!processing && trackIgnore ? (
              <button
                className="rounded ml-sm w-[10em] px-sm py-xs text-center text-body-sm font-bold bg-success-default text-white hover:bg-success-hover"
                onClick={handleTrackIgnore}
              >
                <Icon
                  type="toggle-off"
                  variant="solid"
                  size="sm"
                  className="mr-sm"
                />
                {t('actions.enable')}
              </button>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-xs p-sm">
          <FieldBlock label="Track Title">
            {data?.trackTitle ?? ''}{' '}
            <OverwriteIcon
              className="ml-xs text-subtle"
              value={overwrites.trackTitle}
            />
          </FieldBlock>
          <FieldValue
            label="Track Ver."
            value={data?.recordingVersion ?? '--'}
          />
          <FieldBlock className="flex items-center" label="ISRC">
            <a
              className="flex cursor-pointer items-center underline text-secondary-default"
              href={`${mothershipUrl}/repertoire/recording/${data?.isrc}`}
              target="_blank"
              rel="noreferrer"
            >
              {data?.isrc}{' '}
              <Icon
                type="external-link"
                variant="solid"
                size="sm"
                className="mr-sm"
              />
            </a>
          </FieldBlock>
          <FieldValue
            label="Repertoir Owner"
            value={data?.repertoireOwner ?? '--'}
          />
          <FieldBlock label="Artist">
            {data?.artist ?? ''}{' '}
            <OverwriteIcon
              className="ml-xs text-subtle"
              value={overwrites.artistName}
            />
          </FieldBlock>
          <FieldValue
            label="Audio Presentation"
            value={escapeAudioPresentation(data?.audioPresentation) ?? '--'}
          />
          <FieldValue label="Asset Type" value={data?.recType ?? '--'} />
          <FieldValue
            label="PA Flag"
            value={data?.paFlag === 'Y' ? 'Yes' : 'No'}
          />
          <FieldValue label="Asset Sub Type" value={data?.recSubType ?? '--'} />
          <FieldValue label="Media Code" value={data?.mediaCode ?? '--'} />
        </div>
      </div>

      <div className="rounded-sm border p-sm bg-level-02 border-emphasis-subtle">
        <h2 className="m-sm px-sm text-body-lg font-semibold text-white">
          {t('Release(s)')}
        </h2>
        <div className="p-sm">
          <table className="w-full border border-emphasis-subtle">
            <thead className="border bg-level-03 border-emphasis-subtle">
              <tr>
                <TableTh>{t('table.GPID')}</TableTh>
                <TableTh>{t('table.GRID')}</TableTh>
                <TableTh>{t('table.albumTitle')}</TableTh>
                <TableTh>{t('table.albumSubTitle')}</TableTh>
                <TableTh>{t('table.releaseType')}</TableTh>
                <TableTh>{t('table.majorLabel')}</TableTh>
                <TableTh>{t('table.presentationLabel')}</TableTh>
                <TableTh>{t('table.audioPres')}</TableTh>
                <TableTh>{t('table.PA')}</TableTh>
                <TableTh className="flex items-center">
                  <Icon
                    type="check-circle"
                    variant="solid"
                    size="sm"
                    className="mr-sm"
                  />
                  {t('table.dbom')}
                </TableTh>
                <TableTh>{t('Comment')}</TableTh>
              </tr>
            </thead>
            <tbody>
              {data?.gpid.map((gpid, i) => {
                return (
                  <tr className="border border-emphasis-subtle" key={gpid}>
                    <TableTd>
                      <a
                        className="flex cursor-pointer items-center underline text-secondary-default"
                        href={`${mothershipUrl}/overview;product=${gpid}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {gpid}
                        <Icon
                          type="external-link"
                          variant="solid"
                          size="sm"
                          className="mr-sm"
                        />
                      </a>
                    </TableTd>
                    <TableTd>{data?.grid[i] ?? '--'}</TableTd>
                    <TableTd>{data?.albumTitle[i] ?? '--'}</TableTd>
                    <TableTd>{data?.albumSubtitle[i] ?? '--'}</TableTd>
                    <TableTd>{data?.releaseType[i] ?? '--'}</TableTd>
                    <TableTd>{data?.majorLabel[i] ?? '--'}</TableTd>
                    <TableTd>{data?.presentationLabel[i] ?? '--'}</TableTd>
                    <TableTd>
                      {data?.productAudioPresentations[i] ?? '--'}
                    </TableTd>
                    <TableTd>{data?.productPaFlags[i] ?? 'N'}</TableTd>
                    <TableTd>{data?.completeDboms[i] ?? '--'}</TableTd>
                    <TableTd>
                      <Comment value={data?.internalComments[i]} />
                    </TableTd>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-sm border p-sm bg-level-02 border-emphasis-subtle">
        <div className="relative py-sm">
          <h2 className="mb-md rounded-md px-md text-body-lg font-semibold text-white">
            {t('body.relatedTracks')}
          </h2>
          <div className="absolute right-md top-0 mt-sm flex h-[10em] text-body-xs text-white">
            <span className="ml-xs font-extralight">{t('Media Code')}:</span>
            <button
              className={cn(
                'ml-xs self-baseline rounded-sm px-xs font-bold',
                typeToggle === 'same' && 'bg-level-05 text-subtle',
              )}
              disabled={typeToggle === 'same'}
              onClick={() => setTypeToggle('same')}
            >
              {t('body.same')}
            </button>
            <button
              className={cn(
                'ml-xs self-baseline rounded-sm px-xs font-bold',
                typeToggle === 'all' && 'bg-level-05 text-subtle',
              )}
              disabled={typeToggle === 'all'}
              onClick={() => setTypeToggle('all')}
            >
              {t('body.all')}
            </button>
          </div>
        </div>
        <div className="p-sm">
          <RelatedTracks
            isrc={props.isrc}
            mediaCode={typeToggle === 'same' ? data?.mediaCode : undefined}
          />
        </div>
      </div>

      <div className="rounded-sm border p-sm bg-level-02 border-emphasis-subtle">
        <h2 className="mb-sm p-sm text-body-lg font-semibold text-white">
          {t('Partners Delivery')}
        </h2>
        <div className="grid grid-cols-4 gap-lg p-sm">
          <FieldValue
            label="Delivered to"
            value={`${data?.partnerCount ?? 0} ${autoPluralize(data?.partnerCount, 'partner', 'partners')}`}
          />{' '}
          <FieldBlock className="col-span-3" label="Delivered to">
            <QuickList values={data?.partnerList} />
          </FieldBlock>
        </div>
      </div>

      <div className="rounded-sm border p-sm bg-level-02 border-emphasis-subtle">
        <h2 className="mb-sm px-sm text-body-lg font-semibold text-white">
          {t('Territories Delivery')}
        </h2>
        <div className="grid grid-cols-2 gap-lg p-sm">
          <div>
            <FieldValue
              label="Delivered to"
              value={`${data?.deliveryTerritoryCount ?? 0} ${autoPluralize(data?.deliveryTerritoryCount, 'territory', 'territories')}`}
            />
            <FieldBlock className="mt-xs" label="">
              <QuickList
                values={data?.deliveryTerritoryList.map((c) =>
                  countryRenderer(c, 'detailed'),
                )}
              />
            </FieldBlock>
          </div>
          <div>
            <FieldValue
              label="Missed territories"
              value={`${data?.missedTerritoriesCountAlt ?? 0} ${autoPluralize(data?.missedTerritoriesCountAlt, 'territory', 'territories')}`}
            />
            <FieldBlock className="mt-xs" label="">
              <QuickList
                values={data?.missedTerritoriesAlt.map((c) =>
                  countryRenderer(c, 'detailed'),
                )}
              />
            </FieldBlock>
          </div>
        </div>
      </div>

      <div className="rounded-sm border p-sm bg-level-02 border-emphasis-subtle">
        <h2 className="mb-sm flex items-center rounded-md px-sm text-body-lg font-semibold text-white">
          {/* <AppleLogo className="mr-1 w-4 h-4 inline-block text-white" /> */}
          <span>{t('body.appleDelivery')}</span>
        </h2>
        <div className="grid grid-cols-2 gap-lg p-sm">
          <div>
            <FieldValue
              label="Delivered to"
              value={`${data?.appleTerritoryCount ?? 0} ${autoPluralize(data?.appleTerritoryCount, 'territory', 'territories')}`}
            />
            <FieldBlock className="mt-xs" label="">
              <QuickList
                values={data?.appleTerritoryList.map((c) =>
                  countryRenderer(c, 'detailed'),
                )}
              />
            </FieldBlock>
          </div>
          <div>
            <FieldValue
              label="Missed territories"
              value={`${data?.missedAppleTerritoriesCountAlt ?? 0} ${autoPluralize(data?.missedAppleTerritoriesCountAlt, 'territory', 'territories')}`}
            />
            <FieldBlock className="mt-xs" label="">
              <QuickList
                values={data?.missedAppleTerritoriesAlt.map((c) =>
                  countryRenderer(c, 'detailed'),
                )}
              />
            </FieldBlock>
          </div>
        </div>
      </div>

      <div className="rounded-sm border p-sm bg-level-02 border-emphasis-subtle">
        <h2 className="mb-sm flex items-center rounded-md px-sm text-body-lg font-semibold text-white">
          {/* <SpotifyLogo className="mr-1 w-4 h-4 inline-block text-white" /> */}
          <span>{t('body.spotifyDelivery')}</span>
        </h2>
        <div className="grid grid-cols-2 gap-lg p-sm">
          <div>
            <FieldValue
              label="Delivered to"
              value={`${data?.spotifyTerritoryCount ?? 0} ${autoPluralize(data?.spotifyTerritoryCount, 'territory', 'territories')}`}
            />
            <FieldBlock className="mt-xs" label="">
              <QuickList
                values={data?.spotifyTerritoryList.map((c) =>
                  countryRenderer(c, 'detailed'),
                )}
              />
            </FieldBlock>
          </div>
          <div>
            <FieldValue
              label="Missed territories"
              value={`${data?.missedSpotifyTerritoriesCountAlt ?? 0} ${autoPluralize(data?.missedSpotifyTerritoriesCountAlt, 'territory', 'territories')}`}
            />
            <FieldBlock className="mt-xs" label="">
              <QuickList
                values={data?.missedSpotifyTerritoriesAlt.map((c) =>
                  countryRenderer(c, 'detailed'),
                )}
              />
            </FieldBlock>
          </div>
        </div>
      </div>

      {overwriteDialog ? (
        <Drawer
          onClose={() => setOverwriteDialog(false)}
          open={true}
          size="md"
          title="Overwrite ISRC"
        >
          <TrackOverwrite isrc={props.isrc} />
        </Drawer>
      ) : null}
    </div>
  );
};

GeneralInfo.displayName = 'GeneralInfo';

export { GeneralInfo };
